import React from 'react'
import {graphql, Link} from 'gatsby'
import Vimeo from '@u-wave/react-vimeo'
import Box from '@mui/material/Box'
import {withPrismicPreview} from 'gatsby-plugin-prismic-previews'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Layout from 'src/components/organisms/Layout'
import SEO from 'src/utils/seo'

import DateMarker from 'src/components/molecules/DateMarker'
import RenderRichText from 'src/utils/renderRichText'
import ShareButton from 'src/components/atoms/ShareButton'
import Logo from 'src/components/atoms/Logo'

import {articlebk, dummyCard} from 'src/assets/images/backgrounds'

import type {ArticleDocument} from '../../../types.generated'

interface Props {
  data: {
    prismicArticle: ArticleDocument
  }
}

const Template = ({data}: Props) => {
  const getSectionTitle = (section: string) => {
    switch (section) {
      case 'press-releases':
        return 'In the Press'
      case 'market-insights':
        return 'Market Insights'
      case 'videos':
        return 'Videos'
      default:
        return 'Article'
    }
  }
  return (
    <Layout>
      <SEO
        title={`NCFX | ${getSectionTitle(data.prismicArticle.tags[0])} - ${data.prismicArticle.data.title.text}`}
        url={data.prismicArticle.url}
        description={`${data.prismicArticle.data.post_body.text.slice(
          0,
          200,
        )}...`}
      />
      <Box sx={{py: 6}}>
        <Container maxWidth="lg" sx={{py: {lg: 10, xs: 15}}}>
          <Typography sx={{mb: 10}}>
            Back to{' '}
            <Link to={`/insights/${data.prismicArticle.tags[0]}`}>
              {getSectionTitle(data.prismicArticle.tags[0])}
            </Link>
          </Typography>
          {data.prismicArticle.data.date && (
            <DateMarker date={data.prismicArticle.data.date} large />
          )}
          <Grid container spacing={2}>
            <Grid item sm={11}>
              <RenderRichText
                field={data.prismicArticle.data.title}
                components={{
                  heading1: ({children}) => (
                    <Typography
                      variant="h3"
                      component="h1"
                      gutterBottom
                      sx={{mt: 2}}
                    >
                      {children}
                    </Typography>
                  ),
                }}
              />
              {data.prismicArticle.data.video?.embed_url ? (
                <Box sx={{py: 2}}>
                  <Vimeo
                    responsive
                    video={data.prismicArticle.data.video.embed_url || ''}
                  />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      background: `url(${
                        data.prismicArticle.data.post_image?.url || articlebk
                      })`,
                      aspectRatio: '12/4',
                      backgroundSize: 'cover',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                      backgroundPosition: 'center',
                      mb: 3,
                    }}
                  >
                    {!data.prismicArticle.data.post_image.url && (
                      <Box sx={{width: '80px'}}>
                        <Logo />
                      </Box>
                    )}
                  </Box>
                </>
              )}
              <Box
                sx={{
                  '& img': {
                    maxWidth: '100%',
                    display: 'block',
                    margin: '0 auto',
                  },
                }}
              >
                <RenderRichText
                  field={data.prismicArticle.data.post_body}
                  components={{
                    heading1: ({children}) => (
                      <Typography variant="h3" component="h1" gutterBottom>
                        {children}
                      </Typography>
                    ),
                    heading2: ({children}) => (
                      <Typography variant="h4" component="h2" gutterBottom>
                        {children}
                      </Typography>
                    ),
                    heading3: ({children}) => (
                      <Typography variant="h5" component="h3" gutterBottom>
                        {children}
                      </Typography>
                    ),
                    heading4: ({children}) => (
                      <Typography variant="h6" component="h4" gutterBottom>
                        {children}
                      </Typography>
                    ),
                  }}
                />
              </Box>
            </Grid>
            <Grid item sm={1}>
              <ShareButton />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Layout>
  )
}

export const pageQuery = graphql`
  query ($uid: String!) {
    prismicArticle(uid: {eq: $uid}) {
      _previewable
      data {
        post_body {
          richText
          text
        }
        post_image {
          alt
          url
        }
        video {
          embed_url
        }
        title {
          text
          richText
        }
        date
      }
      tags
      uid
      url
    }
  }
`

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default withPrismicPreview(Template)
